import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getSocietyAdminData,
  getPendingUsers,
  approveUser,
  rejectUser,
  PendingUser,
} from '../services/api';
import { HiClock, HiRefresh, HiSearch, HiPhone, HiCheck, HiX, HiUserGroup } from '../components/Icons';
import './SocietyAdminPendingRequests.css';

function flatLabel(flatNumber?: string, blockName?: string | null): string {
  if (!flatNumber) return '—';
  return blockName ? `${flatNumber}, ${blockName}` : flatNumber;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function requestTypeLabel(user: PendingUser): string {
  if (user.isUserSocietyApproved === 3) {
    return 'Tenant approval (owner approved)';
  }
  if (user.role_in_flat === 'tenant') {
    return 'Tenant join request';
  }
  if (user.role_in_flat === 'co_owner') {
    return 'Co-owner join request';
  }
  return 'Society join request';
}

function pendingRoleLabel(role?: string | null): string {
  switch ((role || '').toLowerCase()) {
    case 'owner':
      return 'Owner';
    case 'co_owner':
      return 'Co-owner';
    case 'tenant':
      return 'Tenant';
    case 'family_member':
      return 'Family';
    default:
      return role ? role.replace(/_/g, ' ') : '';
  }
}

const SocietyAdminPendingRequests: React.FC = () => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [areaByUserId, setAreaByUserId] = useState<Record<number, string>>({});

  const fetchData = useCallback(async () => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getPendingUsers(societyId);
      if (res.success && res.data) {
        const users = [...(res.data.pendingUsers || [])].sort(
          (a, b) =>
            new Date(b.requested_date || 0).getTime() - new Date(a.requested_date || 0).getTime(),
        );
        setPendingUsers(users);
        const areaMap: Record<number, string> = {};
        users.forEach((u) => {
          if (u.flat_id != null) {
            areaMap[u.user_id] =
              u.area != null && u.area > 0 ? String(u.area) : '';
          }
        });
        setAreaByUserId(areaMap);
      } else {
        setPendingUsers([]);
        setAreaByUserId({});
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const q = searchQuery.toLowerCase().trim();
  const filteredUsers = useMemo(() => {
    const sorted = [...pendingUsers].sort(
      (a, b) =>
        new Date(b.requested_date || 0).getTime() - new Date(a.requested_date || 0).getTime(),
    );
    if (!q) return sorted;
    return sorted.filter(
      (u) =>
        u.user_name.toLowerCase().includes(q) ||
        (u.user_phone || '').includes(q) ||
        (u.user_email || '').toLowerCase().includes(q) ||
        flatLabel(u.flat_number, u.block_name).toLowerCase().includes(q) ||
        (u.role_in_flat || '').toLowerCase().includes(q)
    );
  }, [pendingUsers, q]);

  const handleApprove = async (user: PendingUser) => {
    let area: number | undefined;
    if (user.flat_id != null) {
      const raw = (areaByUserId[user.user_id] ?? '').trim();
      if (!raw) {
        setAlert({ type: 'error', message: 'Please enter the flat area (sq.ft) before approving.' });
        return;
      }
      area = parseFloat(raw);
      if (Number.isNaN(area) || area <= 0) {
        setAlert({ type: 'error', message: 'Please enter a valid sq.ft area greater than 0.' });
        return;
      }
      area = Math.round(area * 100) / 100;
    }

    setActionLoadingId(user.user_id);
    try {
      const res = await approveUser(user.user_id, area !== undefined ? { area } : undefined);
      if (res.success) {
        setAlert({ type: 'success', message: res.message || 'Member approved successfully' });
        await fetchData();
      } else {
        setAlert({ type: 'error', message: res.error || 'Failed to approve member' });
      }
    } catch (err: unknown) {
      setAlert({ type: 'error', message: err instanceof Error ? err.message : 'Failed to approve member' });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (user: PendingUser) => {
    if (!societyId) return;
    if (
      !window.confirm(
        `Reject ${user.user_name}'s request to join the society? This cannot be undone.`
      )
    ) {
      return;
    }

    setActionLoadingId(user.user_id);
    try {
      const res = await rejectUser(user.user_id, societyId);
      if (res.success) {
        setAlert({ type: 'success', message: res.message || 'Request rejected' });
        await fetchData();
      } else {
        setAlert({ type: 'error', message: res.error || 'Failed to reject request' });
      }
    } catch (err: unknown) {
      setAlert({ type: 'error', message: err instanceof Error ? err.message : 'Failed to reject request' });
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="sapr-loading">
        <div className="sapr-spinner" />
        <p>Loading pending requests…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sapr-error">
        <p>{error}</p>
        <button type="button" className="sapr-btn sapr-btn-primary" onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="sapr-container">
      {alert && (
        <div className={`sapr-alert sapr-alert-${alert.type}`}>
          <span>{alert.message}</span>
          <button type="button" className="sapr-alert-close" onClick={() => setAlert(null)}>
            &times;
          </button>
        </div>
      )}

      <header className="sapr-header">
        <div className="sapr-header-left">
          <HiClock className="sapr-header-icon" />
          <h1>Pending Requests</h1>
        </div>
        <button type="button" className="sapr-btn sapr-btn-outline" onClick={fetchData} disabled={!!actionLoadingId}>
          <HiRefresh /> Refresh
        </button>
      </header>

      <p className="sapr-intro">
        Review and approve residents who have requested to join your society. For pending bill payment
        approvals, go to{' '}
        <Link to="/society/bill-payments">Bill Payments</Link>.
      </p>

      <div className="sapr-toolbar">
        <div className="sapr-search-bar">
          <HiSearch className="sapr-search-icon" />
          <input
            type="text"
            placeholder="Search by name, phone, flat…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {pendingUsers.length > 0 && (
          <span className="sapr-count-badge">
            {pendingUsers.length} pending
          </span>
        )}
      </div>

      {filteredUsers.length === 0 ? (
        <div className="sapr-empty">
          <HiUserGroup className="sapr-empty-icon" />
          <h2>No pending membership requests</h2>
          <p>
            {pendingUsers.length === 0
              ? 'All join requests have been processed.'
              : 'No requests match your search.'}
          </p>
        </div>
      ) : (
        <div className="sapr-list">
          {filteredUsers.map((user) => {
            const isOwnerApproved = user.isUserSocietyApproved === 3;
            const busy = actionLoadingId === user.user_id;

            return (
              <article key={user.user_id} className="sapr-card">
                <div
                  className={`sapr-card-header ${isOwnerApproved ? 'sapr-card-header-owner-approved' : ''}`}
                >
                  <span className="sapr-card-header-title">{requestTypeLabel(user)}</span>
                  <span className="sapr-card-header-date">
                    Requested {formatDate(user.requested_date)}
                  </span>
                </div>
                <div className="sapr-card-body">
                  <div className="sapr-user-row">
                    <h2 className="sapr-user-name">{user.user_name}</h2>
                  </div>

                  <div className="sapr-details-grid">
                    <div>
                      <div className="sapr-detail-label">Phone</div>
                      <div className="sapr-detail-value">
                        {user.user_phone ? (
                          <>
                            <HiPhone style={{ width: 14, height: 14, verticalAlign: 'middle' }} />{' '}
                            {user.user_phone}
                          </>
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>
                    {user.user_email && (
                      <div>
                        <div className="sapr-detail-label">Email</div>
                        <div className="sapr-detail-value">{user.user_email}</div>
                      </div>
                    )}
                    {user.flat_number && (
                      <div>
                        <div className="sapr-detail-label">Flat</div>
                        <div className="sapr-detail-value">
                          {flatLabel(user.flat_number, user.block_name)}
                        </div>
                      </div>
                    )}
                    {user.role_in_flat && (
                      <div>
                        <div className="sapr-detail-label">Role in flat</div>
                        <span className="sapr-role-badge">{pendingRoleLabel(user.role_in_flat)}</span>
                      </div>
                    )}
                  </div>

                  {user.flat_id != null && (
                    <div className="sapr-area-field">
                      <label htmlFor={`area-${user.user_id}`}>Flat area (sq.ft) *</label>
                      <p className="sapr-area-hint">
                        Review and update before approving — saved to the flat record.
                      </p>
                      <input
                        id={`area-${user.user_id}`}
                        type="text"
                        inputMode="decimal"
                        placeholder="e.g. 1200"
                        value={areaByUserId[user.user_id] ?? ''}
                        disabled={busy}
                        onChange={(e) =>
                          setAreaByUserId((prev) => ({
                            ...prev,
                            [user.user_id]: e.target.value.replace(/[^0-9.]/g, ''),
                          }))
                        }
                      />
                    </div>
                  )}

                  <div className="sapr-actions">
                    <button
                      type="button"
                      className="sapr-btn sapr-btn-primary sapr-btn-sm"
                      disabled={busy}
                      onClick={() => handleApprove(user)}
                    >
                      <HiCheck /> Approve
                    </button>
                    <button
                      type="button"
                      className="sapr-btn sapr-btn-danger-outline sapr-btn-sm"
                      disabled={busy}
                      onClick={() => handleReject(user)}
                    >
                      <HiX /> Reject
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SocietyAdminPendingRequests;
